<i18n src="./translations"></i18n>

<template>
  <div class="wishlist" :data-preload="[$options.name]">
    <WishlistHeader
      :is-shared-wishlist="isSharedWishlist"
      :product-count="productCountWishlist"
      data-testid="WishlistHeader"
    />
    <template v-if="productCountWishlist">
      <WishlistProductSection
        v-for="(section, index) of productSections"
        :key="section.vehicle"
        data-testid="WishlistProductSection"
        class="wishlist__product-section"
        :class="{ 'wishlist__product-section--without-margin': index === 0 }"
        :title="section.vehicle"
        :product-actions="[]"
        :products="section.products"
        :product-marked-for-deletion="productMarkedForDeletion"
        :is-shared-wishlist="isSharedWishlist"
        @quantity-change="handleQuantityChange"
        @action="
          (event) => handleAction({ ...event, vehicle: section.vehicle })
        "
      />
    </template>

    <Dialog
      ref="deleteDialog"
      :title="$t('delete-dialog.title')"
      class="confirm-delete-dialog"
      :confirm-button="{
        title: this.$t('delete-dialog.confirm-delete'),
        onClick: () => {
          confirmDelete();
        },
      }"
      :cancel-button="{
        title: this.$t('delete-dialog.cancel'),
        onClick: () => {
          productMarkedForDeletion = null;
        },
      }"
      @hide="cancelDialog()"
    >
      <div class="dialog_content">
        <p>{{ $t('delete-dialog.content') }}</p>
      </div>
    </Dialog>

    <Dialog
      ref="unavailableProductDialog"
      class="unavailableProductDialog"
      :click-outside="true"
      :title="$t('availability.title')"
    >
      <div>
        {{
          $t('availability.message', {
            '%customerServicePhoneNumber%': customerServicePhoneNumber,
          })
        }}
      </div>
    </Dialog>
  </div>
</template>

<script>
import WishlistHeader from 'Components/02-molecules/wishlist-header/WishlistHeader.vue';
import Dialog from 'Components/02-molecules/dialog/Dialog.vue';
import globalMixin from 'Libs/mixins/globalMixin';
import { mapActions, mapGetters, mapState } from 'vuex';
import WishlistProductSection from 'Components/02-molecules/wishlist-product-section/WishlistProductSection.vue';

export default {
  name: 'Wishlist',
  components: { WishlistHeader, WishlistProductSection, Dialog },
  mixins: [globalMixin],
  props: {
    wishlist: {
      type: Object,
      default: () => {},
      required: true,
    },
  },
  data() {
    return {
      productMarkedForDeletion: null,
      isDeleteLoading: false,
    };
  },
  computed: {
    ...mapState('wishlist', ['sections', 'productCount']),
    ...mapGetters('miniCart', { miniCartProducts: 'getMiniCartProducts' }),
    ...mapGetters('core', ['customerServicePhoneNumber']),
    isSharedWishlist() {
      return this.wishlist.isSharedWishlist || false;
    },
    productSections() {
      return this.wishlist.isSharedWishlist
        ? this.wishlist.sections
        : this.sections;
    },
    productCountWishlist() {
      return this.wishlist.isSharedWishlist
        ? this.wishlist.productCount
        : this.productCount;
    },
  },
  methods: {
    ...mapActions('wishlist', [
      'updateQuantity',
      'addToCartFromWishlist',
      'addFromWishlist',
      'addToWishlist',
    ]),
    ...mapActions('miniCart', ['addToCart']),
    ...mapActions('cart', ['updateInWishlistProp']),
    handleAction({ type, product, vehicle }) {
      if (type === 'add-to-cart') {
        if (!this.checkAvailability(product)) {
          // Prevent adding to cart if the product is not available
          return;
        }

        this.addToCart({
          product: {
            sku: product.sku,
            carTypeNumber: product.carTypeNumber,
            productId: product.productId,
            amount: product.quantityAmount,
          },
          quantity: product.quantityAmount,
        });
      } else if (type === 'delete') {
        this.productMarkedForDeletion = product;
        this.$refs.deleteDialog.show();

        /**
         * [andrei] For the sake of reactivity and UX we will update the inWishlist property of a product which is in cart,
         * without refreshing the page or making another request.
         */
        const productInMiniCartIndex = this.miniCartProducts.findIndex(
          ({ productId, carTypeNumber }) =>
            product.productId === productId &&
            product.carTypeNumber === carTypeNumber
        );

        if (productInMiniCartIndex >= 0) {
          this.updateInWishlistProp({ product, vehicle });
        }
      } else if (type === 'wishlist') {
        this.addToWishlist({
          product: product,
          quantity: product.quantityAmount,
          list: 'Shared Wishlist',
        });
        // // [izerozlu] `amount` parameter's looking a bit weird but this is how the product is being added or removed to/from wishlist.
        // this.addFromWishlist({
        //   sku: product.sku,
        //   carTypeNumber: product.carTypeNumber,
        //   productId: product.productId,
        //   amount: product.inWishlist ? 0 : 1,
        // });
      }
    },
    async confirmDelete() {
      const product = this.productMarkedForDeletion;
      this.isDeleteLoading = true;

      await this.updateQuantity({
        sku: product.sku,
        carTypeNumber: product.carTypeNumber,
        productId: product.productId,
        amount: 0,
      });

      this.productMarkedForDeletion = null;
      this.isDeleteLoading = false;
    },
    cancelDialog() {
      if (this.isDeleteLoading) return;

      this.productMarkedForDeletion = null;
    },
    handleQuantityChange({ product, quantity }) {
      this.updateQuantity({
        sku: product.sku,
        carTypeNumber: product.carTypeNumber,
        productId: product.productId,
        amount: quantity,
      });
    },
    checkAvailability(product) {
      if (
        product.unitPrices?.[0]?.price === 0 ||
        [
          'DELAYED',
          'AVAILABLE_SOON',
          'AVAILABLE_ON_REQUEST',
          'OUT_OF_STOCK',
          'NOT_AVAILABLE',
          'NOTAVAILABLE',
          '2TO4WEEKS',
        ].includes(
          product.availability.key || String(product.availability).toUpperCase()
        )
      ) {
        this.$refs.unavailableProductDialog.show();
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'variables/breakpoints';

.wishlist {
  width: 100%;
}

.wishlist-header {
  width: 100%;
}

.wishlist__product-section {
  width: 100%;

  &:not(.wishlist__product-section--without-margin) {
    margin-top: var(--space-2);
  }
}

@media #{$_mediaMDown} {
  .wishlist {
    padding-top: var(--space-2);
  }
}
</style>
